import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Building2, KeyRound, Languages, LogOut, Menu, Search } from 'lucide-react';
import { useTheme } from '@course-authoring/shared-types';
import {
  adminButton,
  adminField,
  adminFocusable,
  AdminNavRailIcon,
  adminTokens,
  type AdminNavIconId,
} from '../../admin-ui';
import { useAuth } from '../../auth';
import { useClientContext } from '../../hooks/useClientContext';
import { useHasAssignedQuestionnaires } from '../../api/questionnaireApi';
import { useAuthorWithAiAvailability } from '../../api/authorWithAiQueries';
import { ChangePasswordModal } from '../ChangePasswordModal';
import { ClientContextBanner } from './ClientContextBanner';
import { ClientContextPicker } from './ClientContextPicker';
import { LanguageContextPicker } from './LanguageContextPicker';

interface AuthoringShellProps {
  children: ReactNode;
}

const NAV_WIDTH = adminTokens.layout.navWidth;
// Collapsed rail: wide enough for the 20px icons with the item padding.
const NAV_COLLAPSED_WIDTH = '68px';

type GlobalNavItem =
  | { id: AdminNavIconId; label: string; to: string; standardOnly?: true; authorWithAi?: true }
  | { id: AdminNavIconId; label: string; disabled: true; standardOnly?: true; authorWithAi?: true }
  | { id: string; divider: true; authorWithAi?: true };

// standardOnly sections aren't client-account scoped; showing them (unfiltered)
// in a client context would imply a scoping that doesn't exist, so they're
// hidden from the panel entirely until the context switches back to Standard.
// authorWithAi entries show only when the server has the feature turned on;
// builds carry their account, so they appear in every content context.
const GLOBAL_NAV_ITEMS: readonly GlobalNavItem[] = [
  { id: 'author-with-ai', label: 'Author with AI', to: '/author-with-ai', authorWithAi: true },
  { id: 'author-with-ai-divider', divider: true, authorWithAi: true },
  { id: 'lessons', label: 'Lessons', to: '/lessons' },
  { id: 'courses', label: 'Courses', to: '/courses' },
  // Questions is account-scoped (NA-403), same as Image Library — visible in every context.
  { id: 'questions', label: 'Questions', to: '/questions' },
  // Centerstage lists Emtrain's public video library, the same in every context.
  { id: 'centerstage', label: 'Centerstage', to: '/centerstage' },
  { id: 'image-library', label: 'Image Library', to: '/image-library' },
  { id: 'maps', label: 'Maps', to: '/maps', standardOnly: true },
  { id: 'tags', label: 'Tags', to: '/tags', standardOnly: true },
  { id: 'users', label: 'Users & Permissions', to: '/users', standardOnly: true },
  { id: 'theme-settings', label: 'Theme Settings', to: '/theme-settings' },
];

export function AuthoringShell({ children }: AuthoringShellProps) {
  const { user, logout } = useAuth();
  const { tokens } = useTheme();
  const location = useLocation();
  const [isNavOpen, setIsNavOpen] = useState(true);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement | null>(null);
  // Editors and the Author with AI conversation scroll inside their own panes.
  // Delivery pages sit under the editor URLs but are management pages, so the
  // content pane must scroll for them.
  const isEditorRoute =
    ((location.pathname.startsWith('/lessons/') ||
      location.pathname.startsWith('/courses/')) &&
      !location.pathname.endsWith('/delivery')) ||
    location.pathname.startsWith('/author-with-ai/builds/');
  const styles = useMemo(() => createStyles(tokens), [tokens]);

  const { clientContext } = useClientContext();
  const showQuestionnaires = useHasAssignedQuestionnaires(clientContext?.id);
  const showAuthorWithAi = useAuthorWithAiAvailability().status === 'available';
  const navItems = useMemo(() => {
    const items = GLOBAL_NAV_ITEMS.filter((item) =>
      (!('standardOnly' in item) || !item.standardOnly || clientContext === null)
      && (!item.authorWithAi || showAuthorWithAi));
    // Hidden for Standard (clientContext === null) and for accounts with 0 assigned questionnaires.
    if (clientContext !== null && showQuestionnaires) {
      items.push({ id: 'assigned-questionnaires', label: 'Questionnaires', to: '/assigned-questionnaires' });
    }
    return items;
  }, [clientContext, showQuestionnaires, showAuthorWithAi]);

  useEffect(() => {
    setIsProfileMenuOpen(false);
    setIsChangePasswordOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isProfileMenuOpen) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      if (
        profileMenuRef.current &&
        event.target instanceof Node &&
        !profileMenuRef.current.contains(event.target)
      ) {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isProfileMenuOpen]);

  return (
    <div style={styles.shell}>
      <header style={styles.topBar}>
        <div style={styles.leadingSection}>
          <button
            type="button"
            onClick={() => setIsNavOpen((current) => !current)}
            style={{ ...styles.iconButton, ...styles.menuToggle }}
            aria-label={isNavOpen ? 'Hide global navigation' : 'Show global navigation'}
            aria-expanded={isNavOpen}
          >
            <Menu size={20} strokeWidth={1.75} aria-hidden="true" />
          </button>
          <h1 style={styles.appTitle}>Emtrain AuthorAI</h1>
        </div>

        <div style={styles.searchArea}>
          <div style={styles.searchField}>
            <div style={styles.searchIcon}>
              <Search size={16} strokeWidth={1.75} aria-hidden="true" />
            </div>
            <input
              type="search"
              placeholder="Search..."
              style={styles.searchInput}
              aria-label="Search"
            />
          </div>
        </div>

        <div style={styles.trailingSection}>
          <div style={styles.contextGroup}>
            <span style={styles.contextLabel}>
              <Languages size={16} strokeWidth={1.75} aria-hidden="true" />
              Language:
            </span>
            <LanguageContextPicker />
          </div>

          <div style={{ ...styles.contextGroup, marginLeft: adminTokens.spacing.md }}>
            <span style={styles.contextLabel}>
              <Building2 size={16} strokeWidth={1.75} aria-hidden="true" />
              Content Context:
            </span>
            <ClientContextPicker />
          </div>

          <div style={{ ...styles.profileMenu, marginLeft: adminTokens.spacing.md }} ref={profileMenuRef}>
            <button
              type="button"
              onClick={() => setIsProfileMenuOpen((current) => !current)}
              style={styles.profileButton}
              aria-label="Open profile menu"
              aria-expanded={isProfileMenuOpen}
            >
              <span style={styles.profileInitials}>{getUserInitials(user?.name)}</span>
            </button>
            {isProfileMenuOpen && (
              <div style={styles.profilePopover}>
                <div style={styles.profileSummary}>
                  <div style={styles.profileName}>{user?.name || 'Unknown User'}</div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setIsChangePasswordOpen(true);
                    setIsProfileMenuOpen(false);
                  }}
                  style={styles.profileMenuItem}
                >
                  <KeyRound size={16} strokeWidth={1.75} aria-hidden="true" />
                  Change Password
                </button>
                <button type="button" onClick={logout} style={styles.profileMenuItem}>
                  <LogOut size={16} strokeWidth={1.75} aria-hidden="true" />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/*
        Outside the <header> on purpose. The top bar is a stacking context (see
        styles.topBar), so a dialog nested inside it would be trapped below the
        side panels at 1100-1200; out here its overlay keeps its own layer. It
        also must not live inside the profile popover, which unmounts on route
        change — ModalShell has to stay mounted to restore focus.
      */}
      <ChangePasswordModal
        isOpen={isChangePasswordOpen}
        onClose={() => setIsChangePasswordOpen(false)}
      />

      <ClientContextBanner />

      <div style={styles.workspace}>
        <aside
          style={{
            ...styles.navRail,
            ...(isNavOpen ? styles.navRailOpen : styles.navRailClosed),
          }}
          aria-label="Global navigation"
        >
            <nav style={styles.navInner}>
              {navItems.map((item) => {
                if ('divider' in item) {
                  return <hr key={item.id} style={styles.navDivider} aria-hidden="true" />;
                }
                if (!('to' in item)) {
                  return (
                    <button
                      key={item.id}
                      type="button"
                      style={{ ...styles.navItem, ...styles.navItemDisabled }}
                      aria-disabled="true"
                      aria-label={isNavOpen ? undefined : item.label}
                      title={isNavOpen ? undefined : item.label}
                    >
                      <AdminNavRailIcon id={item.id} color={item.id === 'author-with-ai' ? adminTokens.brand.roseForeground : undefined} />
                      <span style={isNavOpen ? styles.navLabel : { ...styles.navLabel, ...styles.navLabelHidden }}>{item.label}</span>
                    </button>
                  );
                }

                return (
                  <NavLink
                    key={item.id}
                    to={item.to}
                    style={({ isActive }) => ({
                      ...styles.navItem,
                      ...(isActive ? styles.navItemActive : {}),
                    })}
                    aria-label={isNavOpen ? undefined : item.label}
                    title={isNavOpen ? undefined : item.label}
                  >
                    {({ isActive }) => (
                      <>
                        {/* Decorative: the selected background, weight and aria-current carry the state. */}
                        {isActive && <span style={styles.navItemMarker} aria-hidden="true" />}
                        <AdminNavRailIcon id={item.id} color={item.id === 'author-with-ai' ? adminTokens.brand.roseForeground : undefined} />
                        <span style={isNavOpen ? styles.navLabel : { ...styles.navLabel, ...styles.navLabelHidden }}>{item.label}</span>
                      </>
                    )}
                  </NavLink>
                );
              })}
            </nav>
        </aside>

        <main
          style={{
            ...styles.contentPane,
            ...(isEditorRoute ? styles.contentPaneEditor : styles.contentPanePage),
          }}
        >
          {children}
        </main>
      </div>
    </div>
  );
}

function getUserInitials(name?: string | null) {
  if (!name) return 'U';

  const initials = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

  return initials || 'U';
}

function createStyles(tokens: ReturnType<typeof useTheme>['tokens']): Record<string, CSSProperties> {
  const a = adminTokens;
  return {
    // The root font and the content pane's background stay on the shared course
    // tokens: editor and preview bodies inherit them, and the admin refresh must
    // not change what they inherit. Admin styling lives on the chrome below.
    shell: {
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: tokens.backgrounds.app,
      fontFamily: tokens.typography.fontFamily,
      overflow: 'hidden',
    },
    topBar: {
      display: 'flex',
      alignItems: 'center',
      gap: a.spacing.lg,
      padding: `${a.spacing.md} ${a.spacing.xl}`,
      backgroundColor: a.surface.panel,
      borderBottom: `1px solid ${a.border.subtle}`,
      color: a.text.primary,
      flexWrap: 'wrap',
      // Deliberately a stacking context: every dropdown in this bar is layered
      // above page content by the bar's own z-index, not by each dropdown
      // picking a high enough value. Page content reaches 10 (list sticky
      // headers), 20 (list row menus) and 101 (CollapsibleNavLayout, via
      // preview); dialogs, overlays and side panels start at 1000 and must stay
      // above the bar. Dialogs opened from this bar therefore have to render
      // OUTSIDE this header — nested inside it they would be trapped below the
      // 1100-1200 panels.
      position: 'relative',
      zIndex: 300,
    },
    leadingSection: {
      display: 'flex',
      alignItems: 'center',
      gap: a.spacing.sm,
      minWidth: 'fit-content',
    },
    // Shifts the 40px button so its icon sits over the nav rail icons (rail icons are centered
    // 34px from the left edge); the negative right margin starts the app title at 56px, the
    // same x as the nav labels.
    menuToggle: {
      marginLeft: '-10px',
      marginRight: '-6px',
    },
    iconButton: {
      ...adminButton.icon,
      width: '40px',
      height: '40px',
      border: 'none',
      backgroundColor: 'transparent',
    },
    appTitle: {
      margin: 0,
      color: a.text.primary,
      fontSize: '17px',
      fontWeight: 600,
      lineHeight: 1.4,
      whiteSpace: 'nowrap',
      // Optical nudge: the cap height sits slightly high against the menu icon.
      position: 'relative',
      top: '1px',
    },
    searchArea: {
      flex: '1 1 280px',
      display: 'flex',
      justifyContent: 'center',
      minWidth: '220px',
    },
    searchField: {
      position: 'relative',
      width: '100%',
      maxWidth: '360px',
    },
    searchIcon: {
      position: 'absolute',
      left: '14px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: a.text.secondary,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      pointerEvents: 'none',
    },
    searchInput: {
      ...adminField.input,
      width: '100%',
      height: '40px',
      borderRadius: a.radius.pill,
      backgroundColor: a.surface.panel,
      padding: `0 ${a.spacing.lg} 0 38px`,
    },
    trailingSection: {
      display: 'flex',
      alignItems: 'center',
      gap: a.spacing.lg,
      marginLeft: 'auto',
      flexWrap: 'wrap',
    },
    contextGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: a.spacing.sm,
      minWidth: 0,
    },
    contextLabel: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      whiteSpace: 'nowrap',
      color: a.text.secondary,
      fontSize: a.typography.label.size,
      fontWeight: 400,
    },
    profileMenu: {
      position: 'relative',
    },
    profileButton: {
      ...adminFocusable,
      width: '40px',
      height: '40px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: a.radius.pill,
      border: 'none',
      backgroundColor: a.surface.subtle,
      color: a.text.primary,
      cursor: 'pointer',
      padding: 0,
    },
    profileInitials: {
      fontSize: a.typography.label.size,
      fontWeight: 700,
      letterSpacing: '0.02em',
    },
    profilePopover: {
      position: 'absolute',
      top: `calc(100% + ${a.spacing.sm})`,
      right: 0,
      width: '232px',
      display: 'flex',
      flexDirection: 'column',
      padding: a.spacing.xs,
      backgroundColor: a.surface.panel,
      border: `1px solid ${a.border.subtle}`,
      borderRadius: a.radius.tile,
      boxShadow: a.shadow.overlay,
      // Peer of the context-picker dropdowns (200); both resolve inside the
      // top bar's stacking context, so this only orders them against each other.
      zIndex: 200,
    },
    profileSummary: {
      padding: `${a.spacing.sm} ${a.spacing.md} ${a.spacing.md}`,
      marginBottom: a.spacing.xs,
      borderBottom: `1px solid ${a.border.subtle}`,
    },
    profileName: {
      color: a.text.primary,
      fontSize: '15px',
      fontWeight: 700,
      overflowWrap: 'anywhere',
    },
    profileMenuItem: {
      ...adminFocusable,
      outlineOffset: '-2px',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      gap: a.spacing.sm,
      padding: `${a.spacing.sm} ${a.spacing.md}`,
      borderRadius: a.radius.control,
      border: 'none',
      backgroundColor: 'transparent',
      color: a.text.primary,
      cursor: 'pointer',
      fontFamily: 'inherit',
      fontSize: a.typography.body.size,
      fontWeight: 600,
      textAlign: 'left',
    },
    workspace: {
      flex: 1,
      display: 'flex',
      minHeight: 0,
      backgroundColor: a.surface.canvas,
    },
    navRail: {
      transition: `width ${tokens.ui.transitionDefault}, border-color ${tokens.ui.transitionDefault}`,
      backgroundColor: a.surface.panel,
      overflow: 'hidden',
      flexShrink: 0,
    },
    navRailOpen: {
      width: NAV_WIDTH,
      borderRight: `1px solid ${a.border.subtle}`,
    },
    navRailClosed: {
      width: NAV_COLLAPSED_WIDTH,
      borderRight: `1px solid ${a.border.subtle}`,
    },
    navInner: {
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: a.spacing.sm,
      padding: `${a.spacing.lg} ${a.spacing.md}`,
      boxSizing: 'border-box',
      overflowY: 'auto',
      // Collapsed rail: the faded labels keep their width, so clip them instead of showing a horizontal scrollbar.
      overflowX: 'hidden',
    },
    navItem: {
      ...adminFocusable,
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      gap: a.spacing.md,
      width: '100%',
      minHeight: '40px',
      padding: `${a.spacing.sm} ${a.spacing.md}`,
      borderRadius: a.radius.nav,
      border: 'none',
      backgroundColor: 'transparent',
      color: a.text.secondary,
      textDecoration: 'none',
      fontFamily: a.typography.fontFamily,
      fontSize: '15px',
      fontWeight: 400,
      lineHeight: 1.3,
      cursor: 'pointer',
      boxSizing: 'border-box',
    },
    navLabel: {
      whiteSpace: 'nowrap',
      opacity: 1,
      transition: `opacity ${tokens.ui.transitionDefault}`,
    },
    navLabelHidden: {
      opacity: 0,
    },
    navItemActive: {
      backgroundColor: a.navigation.selected,
      color: a.text.primary,
      fontWeight: 500,
    },
    navItemMarker: {
      position: 'absolute',
      left: `-${a.spacing.md}`,
      top: '50%',
      width: '3px',
      height: '20px',
      transform: 'translateY(-50%)',
      borderRadius: '0 3px 3px 0',
      backgroundColor: a.brand.marker,
    },
    navDivider: {
      width: '100%',
      height: '1px',
      margin: `${a.spacing.xs} 0`,
      border: 'none',
      backgroundColor: a.border.subtle,
    },
    navItemDisabled: {
      color: a.text.disabled,
      cursor: 'default',
    },
    contentPane: {
      flex: 1,
      minHeight: 0,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: tokens.backgrounds.page,
    },
    contentPanePage: {
      overflow: 'auto',
    },
    contentPaneEditor: {
      overflow: 'hidden',
    },
  };
}
